import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer";
import { sanitizeHTML } from '../../utils/sanitize';

/* ═══════════════════════════════════════════════════════════════
   UNE JOURNÉE À VALOUSE — Expérience immersive
   Style : Aman / Six Senses — minimaliste, contemplatif, vaste
   ═══════════════════════════════════════════════════════════════ */

/* ── Mini-carrousel auto (crossfade) ── */
const AutoCarousel = ({ images, interval = 2000 }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), interval);
    return () => clearInterval(t);
  }, [images.length, interval]);
  return (
    <div className="relative max-w-3xl mx-auto overflow-hidden rounded-xl" style={{ aspectRatio: "3/4" }}>
      {images.map((img, i) => (
        <img
          key={`fragment-${i}`}
          src={img.src}
          alt={img.alt}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: i === idx ? 1 : 0, transition: "opacity 1.5s ease-in-out" }}
          loading={i === 0 ? "eager" : "lazy"}
          data-testid={`intro-interior-img-${i + 1}`}
        />
      ))}
    </div>
  );
};

/* ── Image pleine largeur ── */
const FullImage = ({ src, alt, position = "center" }) => (
  <div className="w-full overflow-hidden">
    <img
      src={src}
      alt={alt}
      className="w-full object-cover"
      style={{ aspectRatio: "21/10", minHeight: "280px", maxHeight: "560px", objectPosition: position }}
      loading="lazy"
    />
  </div>
);

/* ── Bloc poétique ── */
const Verse = ({ lines, accent = false }) => (
  <div
    className="font-serif text-base sm:text-lg leading-[2.4] space-y-7"
    style={{ color: accent ? "#C5A059" : "rgba(63,42,36,0.72)" }}
  >
    {lines.map((line, i) => (
      <p
        key={`fragment-${i}`}
        className="text-center"
        style={{ textWrap: "balance" }}
        dangerouslySetInnerHTML={sanitizeHTML(line)}
      />
    ))}
  </div>
);

/* ── Respiration (espace vide) ── */
const Breath = ({ size = "md" }) => {
  const h = { sm: "h-16 sm:h-20", md: "h-24 sm:h-36", lg: "h-32 sm:h-48" };
  return <div className={h[size]} />;
};

/* ── Titre de moment ── */
const MomentTitle = ({ label }) => (
  <p
    className="font-serif text-lg sm:text-xl tracking-wide text-[#C5A059] text-center"
    data-testid={`moment-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    {label}
  </p>
);

/* ═══════════════════════════════════════════════════════════════ */
/*  PAGE                                                          */
/* ═══════════════════════════════════════════════════════════════ */
const FragmentsPassage = () => {
  const topRef = useRef(null);

  useEffect(() => {
    document.title =
      "Une journée à Valouse | Retraite immersive COCON – Drôme Provençale";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      ref={topRef}
      className="min-h-screen bg-[#F9F7F2] text-[#3F2A24]"
      data-testid="fragments-page"
    >
      {/* ═══════════════════════════════════════════ */}
      {/*  1. INTRO — ENTRÉE IMMERSIVE               */}
      {/* ═══════════════════════════════════════════ */}
      <section
        className="min-h-[85vh] flex items-center justify-center px-6 bg-[#7e7472]"
        data-testid="fragments-intro"
      >
        <div className="max-w-lg mx-auto text-center space-y-10">
          <div className="font-serif text-base sm:text-lg text-[#F3E5CF]/80 leading-[2.6] space-y-8">
            <p className="text-[#F3E5CF] italic text-lg sm:text-xl">
              Et si, juste un instant, tu y étais déjà…
            </p>

            <div className="w-8 h-[1px] bg-[#E8D2B5]/20 mx-auto" />

            <p>
              Le bruit s'éloigne.
              <br />
              Pas parce qu'on te le demande.
              <br />
              Parce que quelque chose en toi n'en a plus besoin.
            </p>

            <p>
              Tu arrives sans vraiment arriver.
              <br />
              Et, déjà, quelque chose commence.
            </p>
          </div>
        </div>
      </section>

      {/* ── Photos intérieures — carrousel crossfade ── */}
      <div className="py-20 sm:py-32 bg-[#7e7472] px-6">
        <AutoCarousel
          interval={2000}
          images={[
            { src: "/images/valouse-interieur/interieur-4.jpg", alt: "Ce qui se vit à l'intérieur – 1" },
            { src: "/images/valouse-interieur/interieur-2.jpg", alt: "Ce qui se vit à l'intérieur – 2" },
            { src: "/images/valouse-interieur/interieur-3.jpg", alt: "Ce qui se vit à l'intérieur – 3" },
            { src: "/images/valouse-interieur/interieur-1.jpg", alt: "Ce qui se vit à l'intérieur – 4" },
          ]}
        />
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/*  2. UNE JOURNÉE À VALOUSE                  */}
      {/* ═══════════════════════════════════════════ */}

      <Breath size="lg" />

      <section className="px-6 text-center" data-testid="journee-titre">
        <div className="max-w-md mx-auto space-y-4">
          <div className="w-12 h-[1px] bg-[#C5A059]/30 mx-auto" />
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#5C4A3D]/60 mt-6">
            La vie intérieure de COCON
          </p>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#3F2A24]">
            Une journée à Valouse
          </h1>
        </div>
      </section>

      <Breath size="lg" />

      {/* ─────────────── ENTRER ─────────────── */}

      <FullImage
        src="https://customer-assets.emergentagent.com/job_ac714315-3cd2-433b-a5fc-ee465f63f309/artifacts/5oe69k4d_IMG_0029-scaled.jpg"
        alt="Retraite immersive France – Arrivée au Hameau de Valouse en Drôme Provençale"
      />

      <Breath size="md" />

      <section className="px-6">
        <div
          className="max-w-xl mx-auto text-center space-y-12"
          style={{ paddingTop: "2rem", paddingBottom: "2rem" }}
        >
          <MomentTitle label="Entrer" />
          <Verse
            lines={[
              "Il n'y a rien à comprendre ici.<br />Rien à réussir.",
              "Juste un seuil invisible que tu franchis.<br />Et, presque sans t'en rendre compte,<br />tu poses quelque chose.",
              "Un rôle.<br />Une tension.<br />Une attente.",
              "Et l'espace s'ouvre.",
            ]}
          />
        </div>
      </section>

      <Breath size="lg" />

      {/* ─────────────── RALENTIR ─────────────── */}

      <FullImage
        src="https://customer-assets.emergentagent.com/job_4d27d25a-3037-4472-8d00-4b545aab579f/artifacts/yzn717i5_DSC05740.jpg"
        alt="Séjour spirituel Drôme – Piscine et cyprès dans la vallée"
      />

      <Breath size="md" />

      <section className="px-6">
        <div
          className="max-w-xl mx-auto text-center space-y-12"
          style={{ paddingTop: "2rem", paddingBottom: "2rem" }}
        >
          <p className="font-serif text-sm sm:text-base text-[#3F2A24]/40 italic">Puis, sans t'en rendre compte…</p>
          <MomentTitle label="Ralentir" />
          <Verse
            lines={[
              "Le rythme change.<br />Pas imposé.<br />Retrouvé.",
              "Le corps reprend sa place.<br />Le souffle s'allonge.<br />Le regard devient plus vaste.",
              "Tu n'es plus en train de suivre.<br />Tu es là.",
              "Simplement là.",
            ]}
          />
        </div>
      </section>

      <Breath size="lg" />

      {/* ─────────────── RENCONTRER ─────────────── */}

      <FullImage
        src="https://customer-assets.emergentagent.com/job_ac714315-3cd2-433b-a5fc-ee465f63f309/artifacts/021uvjka_DSC06818.jpg"
        alt="Retraite COCON – Rencontre et partage sous les arbres à Valouse"
        position="center 37%"
      />

      <Breath size="md" />

      <section className="px-6">
        <div
          className="max-w-xl mx-auto text-center space-y-12"
          style={{ paddingTop: "2rem", paddingBottom: "2rem" }}
        >
          <p className="font-serif text-sm sm:text-base text-[#3F2A24]/40 italic">Et quelque chose s'ouvre.</p>
          <MomentTitle label="Rencontrer" />
          <Verse
            lines={[
              "Les mots sont rares.<br />Mais quand ils viennent, ils sont vrais.",
              "Les regards ne cherchent rien.<br />Et c'est pour ça qu'ils touchent.",
              "Il y a toi.<br />Il y a les autres.<br />Et quelque chose entre, qui circule.",
              "Sans effort.<br />Sans masque.",
            ]}
          />
        </div>
      </section>

      <Breath size="lg" />

      {/* ─────────────── BASCULER ─────────────── */}

      <FullImage
        src="https://customer-assets.emergentagent.com/job_ac714315-3cd2-433b-a5fc-ee465f63f309/artifacts/uojl024q_DSC06578.jpg"
        alt="Retraite transformation – Lâcher-prise et expression corporelle à Valouse"
        position="center 39%"
      />

      <Breath size="md" />

      <section className="px-6">
        <div
          className="max-w-xl mx-auto text-center space-y-12"
          style={{ paddingTop: "2rem", paddingBottom: "2rem" }}
        >
          <p className="font-serif text-sm sm:text-base text-[#3F2A24]/40 italic">Et à un moment…</p>
          <MomentTitle label="Basculer" />
          <Verse
            lines={[
              "À un moment, sans prévenir,<br />quelque chose cède.",
              "Pas spectaculaire.<br />Pas bruyant.",
              "Juste… différent.<br />Plus juste.",
              "Un relâchement.<br />Une évidence.<br />Un endroit en toi qui respire enfin autrement.",
              "Et tu sais, sans avoir besoin de le dire,<br />que tu ne reviendras pas exactement pareil·e.",
            ]}
          />
        </div>
      </section>

      <Breath size="lg" />

      {/* ─────────────── LAISSER UNE TRACE ─────────────── */}

      <FullImage
        src="https://customer-assets.emergentagent.com/job_ac714315-3cd2-433b-a5fc-ee465f63f309/artifacts/i12l2kzl_DSC07656.jpg"
        alt="Retraite COCON – Mains sur le cœur, émotion et gratitude à Valouse"
        position="center 40%"
      />

      <Breath size="md" />

      <section className="px-6">
        <div
          className="max-w-xl mx-auto text-center space-y-12"
          style={{ paddingTop: "2rem", paddingBottom: "2rem" }}
        >
          <p className="font-serif text-sm sm:text-base text-[#3F2A24]/40 italic">Et ça ne s'arrête pas là.</p>
          <MomentTitle label="Laisser une trace" />
          <Verse
            lines={[
              "La journée se termine.<br />Mais pas vraiment.",
              "Quelque chose reste.<br />Dans le corps.<br />Dans les choix.<br />Dans la manière de regarder, ensuite.",
              "Ce n'est pas une expérience que tu ranges.<br />C'est quelque chose que tu emmènes.",
              "Sans effort.<br />Sans intention.",
              "Ça continue de vivre.",
            ]}
          />
        </div>
      </section>

      <Breath size="lg" />

      {/* ─── Murmure final ─── */}
      <section className="py-20 sm:py-28 px-6 text-center">
        <div className="max-w-md mx-auto">
          <p className="font-serif text-base sm:text-lg text-[#3F2A24]/40 italic leading-[2.4]">
            …
            <br />
            <br />
            Et peut-être que, déjà,
            <br />
            tu le sens.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/*  3. RÉSONANCE — SECTION FINALE             */}
      {/* ═══════════════════════════════════════════ */}
      <section
        className="py-28 sm:py-40 px-6 bg-[#7e7472]"
        data-testid="fragments-resonance"
      >
        <div className="max-w-lg mx-auto text-center space-y-14">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#F3E5CF]">
            Alors écoute simplement.
          </h2>

          <div className="w-10 h-[1px] bg-[#E8D2B5]/20 mx-auto" />

          <div className="font-serif text-base sm:text-lg text-[#F3E5CF]/75 leading-[2.6] space-y-8">
            <p>
              Si en lisant ces lignes
              <br />
              quelque chose en toi s'ouvre,
            </p>
            <p className="text-[#F3E5CF]/50">
              sans que tu saches vraiment pourquoi,
            </p>
            <p>
              si une partie de toi reconnaît
              <br />
              ce rythme,
              <br />
              ce silence,
              <br />
              cette manière d'être là,
            </p>
            <p className="text-[#F3E5CF]/50">
              surtout, ne cherche pas à comprendre.
            </p>
            <p className="text-[#F3E5CF]">
              Laisse juste cette sensation exister.
            </p>
            <p className="text-[#C5A059] italic">Elle sait.</p>
          </div>

          <div className="w-10 h-[1px] bg-[#E8D2B5]/20 mx-auto" />

          <div className="space-y-6">
            <p className="font-serif text-base sm:text-lg text-[#F3E5CF]/60 leading-[2.4]">
              Et peut-être que ce que tu cherches
              <br />
              ne s'explique pas.
            </p>
            <p className="font-serif text-lg sm:text-xl text-[#F3E5CF] italic">
              Mais se vit.
            </p>
            <p className="font-serif text-base sm:text-lg text-[#F3E5CF]/80 mt-4">
              Et tu le sais déjà.
            </p>
          </div>

          {/* CTA Final */}
          <div className="pt-10">
            <Link
              to="/valouse#formules"
              className="inline-flex items-center justify-center px-12 py-5 rounded-full bg-[#C5A059]/80 text-[#3F2A24] text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#C5A059] transition-colors duration-500"
              data-testid="cta-decouvrir-experience"
            >
              Je choisis mon expérience
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FragmentsPassage;
